import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import axios from "axios";
import API_BASE_URL from "../config";
import { Card, Row, Col } from "react-bootstrap";
import AdminNavbar from "../Layout/AdminNavbar";
import {
    FaUserPlus,
    FaUsers,
    FaFileAlt,
    FaUserFriends,
} from "react-icons/fa";

const EmployeeLandingPage = () => {
    const navigate = useNavigate();


    const [departmentData, setDepartmentData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const token = localStorage.getItem("token"); // ⬅️ read token

                const res = await axios.get(`${API_BASE_URL}/api/employees`, {
                    headers: {
                        Authorization: `Bearer ${token}`, // ⬅️ add token to header
                    },
                });

                const employees = res.data;

                const deptMap = {};

                employees.forEach(emp => {
                    const dept = emp.department || "Unknown";
                    deptMap[dept] = (deptMap[dept] || 0) + 1;
                });

                const chartData = Object.keys(deptMap).map(dept => ({
                    name: dept,
                    value: deptMap[dept],
                }));

                setDepartmentData(chartData);
            } catch (err) {
                console.error(err);
                toast.error("Unable to load department data");
            } finally {
                setLoading(false);
            }
        };

        fetchEmployees();
    }, []);


    const COLORS = [
        "#3aa89d",
        "#4a6cf7",
        "#7c3aed",
        "#d63384",
        "#f25c3d",
        "#ffc107",
        "#20c997"
    ];

    const employeeLinks = [
        {
            name: "New",
            path: "/addemployee",
            icon: <FaUserPlus size={22} className="text-dark" />,
        },
        {
            name: "List",
            path: "/allemployee",
            icon: <FaUsers size={22} className="text-dark" />,
        },
        {
            name: "Documents",
            path: "/viewalldocs",
            icon: <FaFileAlt size={22} className="text-dark" />,
        },
        {
            name: "Team Management",
            path: "/adminmanageteams",
            icon: <FaUserFriends size={22} className="text-dark" />,
        },
    ];

    // 🔐 Authentication check
    useEffect(() => {
        const token = sessionStorage.getItem("token");
        if (!token) {
            toast.error("You must be logged in to access this page.");
            navigate("/");
        }
    }, [navigate]);

    // ✅ Load Bootstrap dynamically
    useEffect(() => {
        const bootstrapLink = document.createElement("link");
        bootstrapLink.rel = "stylesheet";
        bootstrapLink.href =
            "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
        document.head.appendChild(bootstrapLink);

        const iconsLink = document.createElement("link");
        iconsLink.rel = "stylesheet";
        iconsLink.href =
            "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
        document.head.appendChild(iconsLink);

        return () => {
            bootstrapLink.remove();
            iconsLink.remove();
        };
    }, []);

    return (
        <>
            {/* 🔹 Fixed Navbar */}
            <div className="fixed-top">
                <AdminNavbar />
            </div>

            {/* 🔹 Full-Width Page */}
            <div
                className="employee-page"
                style={{
                    minHeight: "100vh",
                    width: "100%",
                    background: "linear-gradient(135deg, #eef1f7 0%, #ffffff 100%)",
                    paddingTop: "140px",
                    paddingBottom: "60px",
                }}
            >
                <div className="container-fluid px-5">


                    <Row className="align-items-center g-5">
                        {/* 🟢 LEFT COLUMN - Info Section */}
                        <Col
                            xs={12}
                            md={5}
                            className="d-flex flex-column justify-content-center text-center text-md-start"
                        >
                            <h1 className="fw-bold mb-3 text-dark">Employee Overview</h1>
                            <p
                                className="text-secondary fs-5"
                                style={{
                                    textAlign: "justify",
                                    lineHeight: "1.7",
                                    maxWidth: "600px",
                                }}
                            >
                                Simplify and streamline your employee management process. Add new employees,
                                view and manage all staff details, handle documents, and organize teams
                                efficiently — all from one centralized module. Empower your operations
                                with structured and consistent employee data management.
                            </p>
                        </Col>


                        {/* 🔵 RIGHT COLUMN - Cards Grid */}
                        <Col xs={12} md={7}>
                            <Row xs={2} sm={3} md={3} lg={4} className="g-3">
                                {employeeLinks.map((item, idx) => (
                                    <Col key={idx}>
                                        <Link
                                            to={item.path}
                                            className="text-decoration-none text-dark"
                                        >
                                            <Card className="shadow-sm h-100 border-0 hover-card text-center py-3 px-2">
                                                <div className="mb-2">{item.icon}</div>
                                                <h6 className="fw-semibold mb-0">{item.name}</h6>
                                            </Card>
                                        </Link>
                                    </Col>
                                ))}
                            </Row>
                        </Col>
                    </Row>

                    {/* ================= EMPLOYEES BY DEPARTMENT (Dynamic) ================= */}
                    <div className="mt-4 mb-5 p-4 bg-white shadow-sm rounded-4">

                        <h3 className="fw-bold mb-4 text-dark">Employees by Department</h3>

                        {loading ? (
                            <p className="text-muted">Loading department data...</p>
                        ) : (
                            <div className="row g-4">

                                {/* LEFT — Donut Chart */}
                                <div className="col-md-5 d-flex justify-content-center">
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={departmentData}
                                                dataKey="value"
                                                nameKey="name"
                                                outerRadius={120}
                                                innerRadius={60}
                                                paddingAngle={3}
                                            >
                                                {departmentData.map((entry, index) => (
                                                    <Cell
                                                        key={index}
                                                        fill={COLORS[index % COLORS.length]}
                                                    />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* RIGHT — Department List */}
                                <div className="col-md-7">
                                    <table className="table table-borderless">
                                        <thead>
                                            <tr className="text-muted">
                                                <th>Department</th>
                                                <th className="text-end">Employees</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {departmentData.map((d, i) => (
                                                <tr key={i}>
                                                    <td>{d.name}</td>
                                                    <td className="text-end">{d.value}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <div className="text-center mt-3">
                                        <Link to="/allemployee" className="btn btn-primary rounded-pill px-4">
                                            See All Employees
                                        </Link>
                                    </div>
                                </div>

                            </div>
                        )}

                    </div>


                </div>

                {/* 🎨 Hover + Card Styles */}
                <style>
                    {`
            .hover-card {
              background: #fff;
              border-radius: 12px;
              transition: all 0.25s ease;
            }
            .hover-card:hover {
              transform: translateY(-4px) scale(1.03);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }
            h6 {
              font-size: 0.95rem;
            }
          `}
                </style>
            </div>
        </>
    );
};

export default EmployeeLandingPage;
